import { Alert, Grid2 as Grid, Stack, Typography } from '@mui/material';
import EditBaseCycleSettings from './EditBaseCycleSettings';
import { useFormContext, TextArea } from 'framework/form';
import { useState } from 'react';
import { useShowContext } from 'framework/show';
import { get } from 'lodash';
import { Button } from 'framework/ui/mui-builder';
import {
  CYCLE_FREQUENCY,
  CYCLE_FREQUENCY_LABELS,
  CYCLE_SETTINGS_STATUS,
  KEEP_CYCLE_DAYS,
  RESUBMIT_CYCLE_DAYS,
} from 'domain/shared/newProject';
import ApprovalRequiredToggler from '../../shared/mainProjectSection/ApprovalRequiredToggler';

const EditCycleSettings = () => {
  const { data, onChangeCallback } = useFormContext();
  const { data: showData } = useShowContext();

  const source = 'settings.cycleActivitySettings';
  const cycleActivitySettings = showData?.getValueBySource(source);
  const cycleFrequency = cycleActivitySettings?.cycleFrequency;
  const cycleStatus = cycleActivitySettings?.cycleSettingsStatus;

  const [rejectedCycleSettingsStep, setRejectedCycleSettingsStep] = useState<
    typeof KEEP_CYCLE_DAYS | typeof RESUBMIT_CYCLE_DAYS
  >();

  // Approved and in approval cycle settings
  const getWeeklyCycleSettings = (isDisabled: boolean) => {
    return (
      <Grid container spacing={2} size={12} sx={{ alignItems: 'center' }}>
        <Grid
          size={4}
          container
          direction={'row'}
          alignItems={'center'}
          justifyContent={'flex-start'}
          sx={{ color: isDisabled ? 'neutral.main' : 'text.primary' }}
        >
          <Typography>Cycle management:</Typography>
          <Typography variant="subtitle1">{CYCLE_FREQUENCY_LABELS[CYCLE_FREQUENCY.WEEKLY]}</Typography>
        </Grid>
        <Grid size={8}>
          <ApprovalRequiredToggler
            source={`${source}.changeCycleFrequency`}
            disabled={isDisabled}
            label={'Request a change of the cycle from weekly to biweekly'}
          />
        </Grid>
        {get(data, `${source}.changeCycleFrequency`) && (
          <Grid size={8}>
            <TextArea
              source={`${source}.changeCycleFrequencyReason`}
              label="Specify the reason why the project cannot operate on the standard weekly cycles"
            />
          </Grid>
        )}
      </Grid>
    );
  };

  const getBiweeklyCycleSettings = (isDisabled: boolean) => {
    return (
      <Grid container spacing={2} size={12} sx={{ alignItems: 'center' }}>
        <Grid
          size={4}
          container
          direction={'row'}
          alignItems={'center'}
          justifyContent={'flex-start'}
          sx={{ color: isDisabled ? 'neutral.main' : 'text.primary' }}
        >
          <Typography>Cycle management:</Typography>
          <Typography variant="subtitle1">{CYCLE_FREQUENCY_LABELS[CYCLE_FREQUENCY.BIWEEKLY]}</Typography>
        </Grid>
        <Grid size={8}>
          <ApprovalRequiredToggler
            source={`${source}.changeCycleFrequency`}
            disabled={isDisabled}
            label={
              <Stack>
                <>Switch to weekly cycles</>
                <Typography color={isDisabled ? 'textSecondary' : 'error'} variant="caption">
                  Weekly cycle will be applied after submission. Switching to biweekly again will require approval.
                </Typography>
              </Stack>
            }
          />
        </Grid>
      </Grid>
    );
  };

  const getApprovedCycleSettings = () => {
    const isDisabled = cycleStatus === CYCLE_SETTINGS_STATUS.IN_APPROVAL;

    return (
      <>
        {isDisabled && (
          <Grid size={12}>
            <Alert severity="warning">Editing is disabled. Request for update is under review now</Alert>
          </Grid>
        )}

        {/* Temporal fix for active projects: cycleFrequency !== CYCLE_FREQUENCY.BIWEEKLY */}
        {cycleFrequency !== CYCLE_FREQUENCY.BIWEEKLY && getWeeklyCycleSettings(isDisabled)}
        {cycleFrequency === CYCLE_FREQUENCY.BIWEEKLY && getBiweeklyCycleSettings(isDisabled)}
        <EditBaseCycleSettings isDisabled={isDisabled} source={source} />
      </>
    );
  };

  // Rejected cycle settings

  const getRejectedCycleStartSettings = () => {
    return (
      <Grid size={12} spacing={1} container direction="column">
        <Grid>
          <Typography color="textSecondary">
            You initiated a request to switch to a biweekly cycle; however, it was declined for the following reason:
          </Typography>
        </Grid>
        <Grid>
          <Typography>{cycleActivitySettings?.cycleRejectionReason}</Typography>
        </Grid>
        <Grid gap={1} spacing={2} container justifyContent="end">
          <Button
            variant="outlined"
            onClick={() => {
              setRejectedCycleSettingsStep(RESUBMIT_CYCLE_DAYS);
              onChangeCallback?.({
                [`${source}.changeCycleFrequency`]: true,
                [`${source}.cycleSettingsStatus`]: CYCLE_SETTINGS_STATUS.APPROVED,
                [`${source}.cycleFrequency`]: CYCLE_FREQUENCY.WEEKLY,
              });
            }}
          >
            Resubmit biweekly cycle request
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setRejectedCycleSettingsStep(KEEP_CYCLE_DAYS);
              onChangeCallback?.({
                [`${source}.changeCycleFrequency`]: true,
                [`${source}.cycleSettingsStatus`]: CYCLE_SETTINGS_STATUS.APPROVED,
                [`${source}.cycleFrequency`]: CYCLE_FREQUENCY.BIWEEKLY,
              });
            }}
          >
            Keep weekly cycle
          </Button>
        </Grid>
      </Grid>
    );
  };

  const getRejectedCycleSettings = () => {
    if (!rejectedCycleSettingsStep) return getRejectedCycleStartSettings();

    return (
      <>
        {rejectedCycleSettingsStep === RESUBMIT_CYCLE_DAYS && (
          <Grid size={8}>
            <TextArea
              source={`${source}.changeCycleFrequencyReason`}
              label="Specify the reason why the project cannot operate on the standard weekly cycles"
            />
          </Grid>
        )}
        <EditBaseCycleSettings isDisabled={false} source={source} />
      </>
    );
  };

  return (
    <Stack spacing={3} pt={2}>
      {cycleStatus === CYCLE_SETTINGS_STATUS.REJECTED ? getRejectedCycleSettings() : getApprovedCycleSettings()}
    </Stack>
  );
};

export default EditCycleSettings;
